import {useEffect, useMemo, useState} from "react";
import {useOptionCalculator} from "../../../context/derivatives/calculator/OptionCalculatorContext";
import {calculateYData} from "../../../utils/optionCalculatorYData";
import OptionCalculatorChart from "./OptionCalculatorChart";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";

const OptionCalculatorCharts = () => {
    const {state} = useOptionCalculator();

    const [totalSellPremium, setTotalSellPremium] = useState(0);
    const [totalBuyPremium, setTotalBuyPremium] = useState(0);

    useEffect(() => {
        const buyPremium = state
            .filter((item) => item.buy_sell === "buy")
            .reduce((sum, item) => sum + Number(item.premium), 0);

        const sellPremium = state
            .filter((item) => item.buy_sell === "sell")
            .reduce((sum, item) => sum + Number(item.premium), 0);

        setTotalBuyPremium(buyPremium);
        setTotalSellPremium(sellPremium);
    }, [state]);

    // biggest strike number in array
    const maxStrikeNumber = useMemo(
        () => Math.max(...state.map((item) => Number(item.strike) || 0)),
        [state]
    );

    // biggest premium number in array
    const maxPremiumNumber = useMemo(
        () => Math.max(...state.map((item) => Number(item.premium) || 0)),
        [state]
    );

    // Generate an x-axis array with exactly N values between 0 and (maxValue * 2)
    // no rounding on number, only shows until 2 decimals
    const generateXAxisData = (maxValue) => {
        if (!maxValue) {
            return [];
        }
        const count = 100;
        const end = maxValue * 2;
        const step = end / (count - 1);
        return Array.from({length: count}, (_, index) =>
            index === count - 1
                ? parseFloat(end.toFixed(2))
                : parseFloat((index * step).toFixed(2))
        );
    };

    // Choose which max value to use based on the type of option/stock
    const xAxisData = useMemo(() => {
        if (state.some((item) => item.call_put_stock === "stock")) {
            return generateXAxisData(maxPremiumNumber);
        } else {
            return generateXAxisData(maxStrikeNumber);
        }
    }, [state, maxPremiumNumber, maxStrikeNumber]);

    // create y-axis data
    const yAxisData = useMemo(() => {
        return xAxisData.map((x) =>
            state.reduce((sum, position) => sum + calculateYData(x, position), 0)
        );
    }, [state, xAxisData]);

    const shouldRenderChart = totalSellPremium <= totalBuyPremium;

    // create y-axis data by percent
    const percentYAxisData = shouldRenderChart
        ? yAxisData.map((y) => (y / (totalBuyPremium - totalSellPremium)) * 100)
        : [];

    const data = xAxisData.map((x, index) => ({
        x,
        y: yAxisData[index],
        percentY: percentYAxisData[index]
    }));

    return (
        <Grid2 container spacing={2}>
            <Grid2 xs={12} lg={6}>
                <OptionCalculatorChart
                    data={data}
                    chartType="normal"
                />
            </Grid2>
            <Grid2 xs={12} lg={6}>
                <OptionCalculatorChart
                    data={data}
                    chartType="percent"
                />
            </Grid2>
        </Grid2>
    );
};

export default OptionCalculatorCharts;
